import {
  Badge,
  Button,
  Checkbox,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { useState } from "react";
import type {
  AdminCatalogItem,
  AdminCatalogProduct,
} from "@/modules/cf-feed/presentation/api/types";
import {
  canSelectCatalogItem,
  updateCatalogSelection,
} from "@/modules/cf-feed/presentation/lib/catalog-add";
import { useBoundedInfiniteScroll } from "@/modules/cf-feed/presentation/lib/use-bounded-infinite-scroll";
import { PreorderProductMeta } from "@/modules/cf-feed/presentation/ui/PreorderProductMeta";
import {
  formatKip,
  Thumb,
} from "@/modules/cf-feed/presentation/ui/table-helpers";

export type CatalogTableProps = {
  data: AdminCatalogItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  total: number;
  inPostVariantIds: Set<string>;
  hasFeed: boolean;
  isAdding: boolean;
  selectedIds: Set<string>;
  onSelectionChange: (selectedIds: Set<string>) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onAdd: (item: AdminCatalogItem) => void;
  products?: AdminCatalogProduct[];
  noDataMessage?: string;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  isItemEligible?: (item: AdminCatalogItem) => boolean;
};

export function CatalogTable({
  data,
  isLoading,
  offset,
  limit,
  total,
  inPostVariantIds,
  hasFeed,
  isAdding,
  selectedIds,
  onSelectionChange,
  onPaginationChange,
  onAdd,
  products,
  noDataMessage = "ບໍ່ພົບສິນຄ້າທີ່ມີສະຕັອກ ຫຼື ເປີດຮັບ preorder",
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  isItemEligible,
}: CatalogTableProps) {
  const { scrollRootRef, sentinelRef } = useBoundedInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
  });
  const [expandedProductIds, setExpandedProductIds] = useState<Set<string>>(
    () => new Set(),
  );
  const canSelectItem = (item: AdminCatalogItem) =>
    canSelectCatalogItem(item, {
      hasFeed,
      inPostVariantIds,
      isAdding,
      isItemEligible,
    });
  const selectableIds = data
    .filter(canSelectItem)
    .map((item) => item.variantId);

  if (products) {
    const groupColumns: TanstackReactTable.ColumnDef<AdminCatalogProduct>[] = [
      {
        id: "product",
        header: "ສິນຄ້າ",
        cell: ({ row }) => {
          const product = row.original;
          const expanded = expandedProductIds.has(product.productId);
          const selectedCount = product.variants.filter((item) =>
            selectedIds.has(item.variantId),
          ).length;
          const addableCount = product.variants.filter((item) =>
            canSelectItem(item),
          ).length;
          const productSelectableIds = product.variants
            .filter(canSelectItem)
            .map((item) => item.variantId);
          const allProductSelected =
            productSelectableIds.length > 0 &&
            productSelectableIds.every((id) => selectedIds.has(id));
          return (
            <div className="min-w-[280px]">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="size-8 shrink-0"
                  aria-label={
                    expanded
                      ? `ຫຸບ variant ຂອງ ${product.name}`
                      : `ເບິ່ງ variant ຂອງ ${product.name}`
                  }
                  aria-expanded={expanded}
                  onClick={() =>
                    setExpandedProductIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(product.productId))
                        next.delete(product.productId);
                      else next.add(product.productId);
                      return next;
                    })
                  }
                >
                  {expanded ? (
                    <ChevronDown className="size-4" />
                  ) : (
                    <ChevronRight className="size-4" />
                  )}
                </Button>
                <Thumb src={product.variants[0]?.imageUrl} alt={product.name} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{product.name}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="font-normal">
                      {product.variants.length} variants
                    </Badge>
                    <span className="text-muted-foreground text-xs">
                      ເພີ່ມໄດ້ {addableCount}
                      {selectedCount > 0 ? ` · ເລືອກ ${selectedCount}` : ""}
                    </span>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={productSelectableIds.length === 0}
                    aria-pressed={allProductSelected}
                    aria-label={`${allProductSelected ? "ຍົກເລີກທັງໝົດ" : "ເລືອກທັງໝົດ"} ${product.name}`}
                    onClick={() =>
                      onSelectionChange(
                        updateCatalogSelection(
                          selectedIds,
                          productSelectableIds,
                          !allProductSelected,
                        ),
                      )
                    }
                  >
                    {allProductSelected ? "ຍົກເລີກທັງໝົດ" : "ເລືອກທັງໝົດ"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setExpandedProductIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(product.productId))
                          next.delete(product.productId);
                        else next.add(product.productId);
                        return next;
                      })
                    }
                  >
                    {expanded ? "ຫຸບ" : "ເບິ່ງ"}
                  </Button>
                </div>
              </div>
              {expanded ? (
                <div className="mt-3 space-y-2 border-muted border-t pt-2">
                  {product.variants.map((item) => {
                    const canAdd = canSelectItem(item);
                    return (
                      <div
                        key={item.variantId}
                        className="flex items-center gap-2 rounded-lg border bg-muted/20 p-2"
                      >
                        {canAdd ? (
                          <Checkbox
                            aria-label={`ເລືອກ ${item.sku}`}
                            checked={selectedIds.has(item.variantId)}
                            onCheckedChange={(value) => {
                              const next = new Set(selectedIds);
                              if (value === true) next.add(item.variantId);
                              else next.delete(item.variantId);
                              onSelectionChange(next);
                            }}
                            onClick={(event) => event.stopPropagation()}
                          />
                        ) : (
                          <span className="size-4 shrink-0" />
                        )}
                        <Thumb src={item.imageUrl} alt={product.name} />
                        <div className="min-w-0 flex-1 text-xs">
                          <p className="font-medium font-mono">{item.sku}</p>
                          <p className="text-muted-foreground">
                            {item.defaultCfCode ??
                              item.suggestedCfCode ??
                              "AUTO"}{" "}
                            · {formatKip(item.price)} ·{" "}
                            {item.isPreorder
                              ? "preorder"
                              : `ເພີ່ມໄດ້ ${item.availableToAdd}`}
                          </p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={!canAdd}
                          onClick={() => onAdd(item)}
                        >
                          <Plus className="size-4" />
                          ເພີ່ມ
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        },
      },
    ];

    return (
      <div
        ref={scrollRootRef}
        className="max-h-[36rem] overflow-auto overscroll-contain"
      >
        <DataTable<AdminCatalogProduct, unknown>
          data={products}
          columns={groupColumns}
          isLoading={isLoading}
          noDataMessage={noDataMessage}
          offset={offset}
          limit={limit}
          totalCount={total}
          enablePagination={!onLoadMore}
          onPaginationChange={(page) =>
            onPaginationChange(page.offset, page.limit)
          }
        />
        {hasNextPage ? (
          <div
            ref={sentinelRef}
            className="flex min-h-10 items-center justify-center text-muted-foreground text-xs"
            aria-live="polite"
          >
            {isFetchingNextPage ? "ກຳລັງໂຫຼດເພີ່ມ..." : "ເລື່ອນເພື່ອໂຫຼດເພີ່ມ"}
          </div>
        ) : null}
      </div>
    );
  }

  const columns: TanstackReactTable.ColumnDef<AdminCatalogItem>[] = [
    {
      id: "select",
      header: () => {
        if (selectableIds.length === 0) return null;
        const allChecked = selectableIds.every((id) => selectedIds.has(id));
        const someChecked = selectableIds.some((id) => selectedIds.has(id));
        return (
          <Checkbox
            aria-label="ເລືອກສິນຄ້າທັງໝົດ"
            checked={allChecked ? true : someChecked ? "indeterminate" : false}
            onCheckedChange={(value) => {
              onSelectionChange(
                updateCatalogSelection(
                  selectedIds,
                  selectableIds,
                  value === true,
                ),
              );
            }}
          />
        );
      },
      cell: ({ row }) => {
        const item = row.original;
        const selectable = canSelectItem(item);
        if (!selectable) return null;
        return (
          <Checkbox
            aria-label={`ເລືອກ ${item.name}`}
            checked={selectedIds.has(item.variantId)}
            onCheckedChange={(value) => {
              const next = new Set(selectedIds);
              if (value === true) next.add(item.variantId);
              else next.delete(item.variantId);
              onSelectionChange(next);
            }}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
      size: 44,
    },
    {
      id: "name",
      header: "ຊື່ສິນຄ້າ",
      cell: ({ row }) => (
        <div>
          <p className="line-clamp-2 font-medium">{row.original.name}</p>
          <p className="text-muted-foreground text-xs">
            SKU: {row.original.sku}
          </p>
          {row.original.isPreorder ? (
            <PreorderProductMeta
              preorderExpectedDate={row.original.preorderExpectedDate}
            />
          ) : null}
        </div>
      ),
    },
    {
      id: "cfCode",
      header: "ລະຫັດ CF",
      cell: ({ row }) => (
        <span className="font-mono text-xs">
          {row.original.defaultCfCode ?? "AUTO"}
        </span>
      ),
      size: 110,
    },
    {
      id: "price",
      header: "ລາຄາ",
      cell: ({ row }) => formatKip(row.original.price),
      size: 120,
    },
    {
      id: "qty",
      header: "ເພີ່ມໄດ້",
      cell: ({ row }) =>
        row.original.isPreorder ? (
          <p className="text-muted-foreground text-xs">ບໍ່ອີງສະຕັອກ</p>
        ) : (
          <div className="text-xs">
            <p className="font-medium">{row.original.availableToAdd}</p>
            <p className="text-muted-foreground">
              ຄັງ {row.original.stockOnHand} · ຈອງ{" "}
              {row.original.quantityReserved ?? 0} · CF{" "}
              {row.original.allocatedByOtherPosts}
            </p>
          </div>
        ),
      size: 120,
    },
    {
      id: "actions",
      header: "ຈັດການ",
      cell: ({ row }) => {
        const item = row.original;
        const canAdd = canSelectItem(item);
        return (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!canAdd}
            onClick={() => onAdd(item)}
          >
            <Plus className="size-4" />
            ເພີ່ມ
          </Button>
        );
      },
      size: 96,
    },
  ];

  return (
    <div
      ref={scrollRootRef}
      className="max-h-[36rem] overflow-auto overscroll-contain"
    >
      <DataTable<AdminCatalogItem, unknown>
        data={data}
        columns={columns}
        isLoading={isLoading}
        noDataMessage={noDataMessage}
        offset={offset}
        limit={limit}
        totalCount={total}
        enablePagination={!onLoadMore}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      />
      {hasNextPage ? (
        <div
          ref={sentinelRef}
          className="flex min-h-10 items-center justify-center text-muted-foreground text-xs"
          aria-live="polite"
        >
          {isFetchingNextPage ? "ກຳລັງໂຫຼດເພີ່ມ..." : "ເລື່ອນເພື່ອໂຫຼດເພີ່ມ"}
        </div>
      ) : null}
    </div>
  );
}
